import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { useFormatAmount } from "../../../home/hooks/useFormatAmount";

interface ChartContainerProps {
  chartData: number[];
  isSalesView: boolean;
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ChartContainer: React.FC<ChartContainerProps> = ({ chartData, isSalesView }) => {
  const data = MONTH_LABELS.map((month, index) => ({
    month,
    value: chartData[index] || 0,
    fill: getBarColor(chartData[index] || 0, index, chartData, isSalesView),
  }));
  const formatAmount = useFormatAmount();

  function getBarColor(value: number, index: number, chartData: number[], isSalesView: boolean) {
    const maxValue = Math.max(...chartData.filter(val => val > 0));
    const intensity = maxValue > 0 ? value / maxValue : 0;

    if (isSalesView) {
      // Emerald gradient with better visual appeal
      const baseColor = [16, 185, 129]; // emerald-500
      const brightness = 0.7 + intensity * 0.3;
      return `rgb(${baseColor[0] * brightness}, ${baseColor[1] * brightness}, ${baseColor[2] * brightness})`;
    } else {
      // Blue gradient with better visual appeal
      const baseColor = [59, 130, 246]; // blue-500
      const brightness = 0.7 + intensity * 0.3;
      return `rgb(${baseColor[0] * brightness}, ${baseColor[1] * brightness}, ${baseColor[2] * brightness})`;
    }
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-xl p-4 relative">
          {/* Tooltip accent line */}
          <div 
            className="absolute left-0 top-0 bottom-0 w-1 rounded-full"
            style={{ 
              backgroundColor: isSalesView ? "#10b981" : "#3b82f6" 
            }}
          />
          <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
          <p className="text-lg font-bold" style={{ color: isSalesView ? "#10b981" : "#3b82f6" }}>
             {isSalesView ? formatAmount(payload[0].value) : payload[0].value.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {isSalesView ? "Total Sales" : "Total Orders"}
          </p>
        </div>
      );
    }
    return null;
  };

  // Calculate some stats for the header
  // const totalValue = data.reduce((sum, item) => sum + item.value, 0);
  // const averageValue = totalValue / data.length;
  // const maxValue = Math.max(...data.map(item => item.value));

  return (
    <div className="flex-1 bg-gradient-to-br from-white to-gray-100/80 rounded-2xl border border-gray-300/60 shadow-xl transition-all duration-500 p-6 min-h-[360px] group relative overflow-hidden">
      
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/50 to-transparent opacity-50"></div>
      <div 
        className="absolute top-0 right-0 w-32 h-32 opacity-5 blur-xl"
        style={{ 
          backgroundColor: isSalesView ? "#10b981" : "#3b82f6" 
        }}
      ></div>

      {/* Header */}
      <div className="relative mb-8">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div 
                className="w-3 h-8 rounded-full shadow-lg"
                style={{ 
                  backgroundColor: isSalesView ? "#10b981" : "#3b82f6",
                  boxShadow: `0 4px 14px 0 ${isSalesView ? 'rgba(16, 185, 129, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`
                }}
              ></div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{isSalesView ? "Sales Performance" : "Order Volume"}</h3>
                <p className="text-sm text-gray-600 mt-1">{isSalesView ? "Monthly revenue trends and insights" : "Monthly order distribution and patterns"}</p>
              </div>
            </div>
            
            {/* Stats summary */}
            {/* <div className="flex items-center gap-6 mt-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {isSalesView ? `QAR ${totalValue.toLocaleString()}` : totalValue.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 uppercase tracking-wider font-medium">Total</div>
              </div>
              <div className="w-px h-8 bg-gray-300"></div>
              <div className="text-center">
                <div className="text-xl font-semibold text-gray-900">
                  {isSalesView ? `QAR ${Math.round(averageValue).toLocaleString()}` : Math.round(averageValue).toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 uppercase tracking-wider font-medium">Average</div>
              </div>
              <div className="w-px h-8 bg-gray-300"></div>
              <div className="text-center">
                <div className="text-xl font-semibold text-gray-900">
                  {isSalesView ? `QAR ${maxValue.toLocaleString()}` : maxValue.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 uppercase tracking-wider font-medium">Peak</div>
              </div>
            </div> */}
          </div>
          
          <div className={`px-4 py-2 rounded-full text-sm font-semibold border-2 backdrop-blur-sm ${
            isSalesView 
              ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/20 shadow-lg shadow-emerald-500/10" 
              : "bg-blue-500/10 text-blue-700 border-blue-500/20 shadow-lg shadow-blue-500/10"
          }`}>
            {isSalesView ? "💰 Revenue" : "📦 Orders"}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="relative h-[170px] w-full mb-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="#f3f4f6" 
              vertical={false}
              strokeOpacity={0.6}
            />
            <XAxis 
              dataKey="month" 
              axisLine={false} 
              tickLine={false} 
              tick={{ fill: "#6b7280", fontSize: 11, fontWeight: 600 }}
              padding={{ left: 10, right: 10 }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#6b7280", fontSize: 11, fontWeight: 600 }}
              width={70}
              tickFormatter={value => (isSalesView ? `QAR ${formatAmount(value)}` : value.toLocaleString())}
            />
            <Tooltip 
              content={<CustomTooltip />} 
              cursor={{ 
                fill: isSalesView ? "rgba(16, 185, 129, 0.1)" : "rgba(59, 130, 246, 0.1)",
                stroke: isSalesView ? "rgba(16, 185, 129, 0.3)" : "rgba(59, 130, 246, 0.3)",
                strokeWidth: 1,
                radius: 4
              }} 
            />
            <Bar 
              dataKey="value" 
              radius={[6, 6, 0, 0]} 
              barSize={36}
              className="transition-all duration-300 hover:shadow-2xl"
            >
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.fill}
                  className="transition-all duration-500 hover:opacity-90 hover:shadow-2xl"
                  style={{
                    filter: `drop-shadow(0 4px 6px ${isSalesView ? 'rgba(16, 185, 129, 0.2)' : 'rgba(59, 130, 246, 0.2)'})`,
                  }}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Enhanced Legend and Footer */}
      <div className="relative flex items-center justify-between mt-4 pt-2 border-t border-gray-200/60">
        <div className="flex items-center space-x-4 text-sm">
          <div className="flex items-center space-x-3">
            <div
              className="w-4 h-4 rounded-md shadow-sm"
              style={{
                backgroundColor: isSalesView ? "#10b981" : "#3b82f6",
                boxShadow: `0 2px 8px 0 ${isSalesView ? 'rgba(16, 185, 129, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`
              }}
            />
            <span className="font-semibold text-gray-700">{isSalesView ? "Sales Amount" : "Order Count"}</span>
          </div>
          <div className="w-px h-4 bg-gray-300"></div>
          <div className="flex items-center space-x-2 text-gray-500">
            <span className="text-xs">📊</span>
            <span className="text-xs font-medium">Interactive chart - hover for details</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full animate-pulse ${
            isSalesView ? "bg-emerald-500" : "bg-blue-500"
          }`}></div>
          <span className="text-xs text-gray-500 font-medium">Live Data</span>
        </div>
      </div>
    </div>
  );
};

export default ChartContainer;